import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ChevronDown, ChevronUp, Loader2, MessagesSquare } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { whatsappApi } from "@/services/whatsapp-api";
import type { WhatsAppItem, WhatsAppTranscriptMessage } from "@/types/whatsapp";

/** Mensagens mostradas antes e depois do trecho citado. */
const CONTEXTO_ANTES = 3;
const CONTEXTO_DEPOIS = 3;

const AUTOR_STYLES: Record<string, string> = {
  CONTATO: "bg-muted",
  ATENDENTE: "bg-blue-50",
  IA: "bg-indigo-50",
  "NOTA INTERNA": "bg-amber-50 border border-dashed border-amber-300",
};

function horaDe(iso: string): string {
  return new Date(iso).toLocaleTimeString("pt-BR", {
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** Recorta a conversa em volta das mensagens que embasaram a conclusão. */
function janelaDeContexto(
  mensagens: WhatsAppTranscriptMessage[],
  messageIds: string[],
): WhatsAppTranscriptMessage[] {
  const citadas = new Set(messageIds);
  const posicoes = mensagens
    .map((mensagem, indice) => (citadas.has(mensagem.id) ? indice : -1))
    .filter((indice) => indice >= 0);
  if (posicoes.length === 0) return [];
  const inicio = Math.max(0, Math.min(...posicoes) - CONTEXTO_ANTES);
  const fim = Math.min(mensagens.length, Math.max(...posicoes) + CONTEXTO_DEPOIS + 1);
  return mensagens.slice(inicio, fim);
}

interface WhatsAppEvidenciaProps {
  item: WhatsAppItem;
  onVerConversaCompleta: (item: WhatsAppItem) => void;
}

export function WhatsAppEvidencia({
  item,
  onVerConversaCompleta,
}: WhatsAppEvidenciaProps) {
  const [expandido, setExpandido] = useState(false);

  // Mesma queryKey do dialog de transcript: o contexto reaproveita o cache.
  const transcriptQuery = useQuery({
    queryKey: ["whatsapp-transcript", item.conversation_id],
    queryFn: () => whatsappApi.getTranscript(item.conversation_id),
    enabled: expandido,
    staleTime: 5 * 60 * 1000,
  });

  const contexto = useMemo(
    () => janelaDeContexto(transcriptQuery.data?.messages ?? [], item.message_ids),
    [transcriptQuery.data, item.message_ids],
  );

  const citadas = new Set(item.message_ids);
  const temAncora = item.message_ids.length > 0;

  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          Evidência na conversa
        </span>
        {temAncora && (
          <Button
            variant="ghost"
            size="sm"
            className="h-7 gap-1.5 px-2 text-xs text-muted-foreground"
            onClick={() => setExpandido((atual) => !atual)}
          >
            {expandido ? (
              <ChevronUp className="h-3.5 w-3.5" />
            ) : (
              <ChevronDown className="h-3.5 w-3.5" />
            )}
            {expandido ? "Ocultar contexto" : "Ver no contexto"}
          </Button>
        )}
      </div>

      {!expandido &&
        item.quotes.map((quote, indice) => (
          <button
            key={indice}
            type="button"
            onClick={() => (temAncora ? setExpandido(true) : onVerConversaCompleta(item))}
            className="block w-full rounded-md border-l-2 border-primary/40 bg-muted/60 px-3 py-1.5 text-left text-sm italic hover:bg-muted"
            title="Ver no contexto da conversa"
          >
            <span className="not-italic text-xs font-medium text-muted-foreground">
              {quote.autor}:
            </span>{" "}
            “{quote.texto}”
          </button>
        ))}

      {expandido && (
        <div className="space-y-1 rounded-md border bg-muted/30 p-2">
          {transcriptQuery.isLoading ? (
            <div className="flex justify-center py-3">
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            </div>
          ) : contexto.length === 0 ? (
            <div className="py-2 text-center text-sm text-muted-foreground">
              Não consegui recuperar o trecho da conversa.
            </div>
          ) : (
            <>
              {contexto.map((mensagem) => {
                const destacada = citadas.has(mensagem.id);
                return (
                  <div
                    key={mensagem.id}
                    className={cn(
                      "rounded-md px-2 py-1.5 text-sm",
                      AUTOR_STYLES[mensagem.autor] ?? "bg-background",
                      destacada && "ring-2 ring-primary",
                      !destacada && "opacity-70",
                    )}
                  >
                    <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
                      <span className="font-medium">{mensagem.autor}</span>
                      <span>{horaDe(mensagem.created_at)}</span>
                    </div>
                    <div className="whitespace-pre-wrap">{mensagem.texto}</div>
                  </div>
                );
              })}
              <Button
                variant="ghost"
                size="sm"
                className="h-7 w-full gap-1.5 text-xs text-muted-foreground"
                onClick={() => onVerConversaCompleta(item)}
              >
                <MessagesSquare className="h-3.5 w-3.5" />
                Ver conversa completa
              </Button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
